import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Menu, X, Book, User, LogOut, Bookmark, History, Settings } from 'lucide-react';
import { useAuthStore, useUIStore, useDomainStore } from '../../hooks/useStore';
import './Header.css';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const searchInputRef = useRef(null);
  const userMenuRef = useRef(null);

  const { user, isAuthenticated, logout } = useAuthStore();
  const { isSearchOpen, toggleSearch, closeSearch, toggleSidebar } = useUIStore();
  const { domain } = useDomainStore();

  const siteName = domain?.name || 'NovelVibe';

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      closeSearch();
    }
  };

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
    navigate('/');
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/genres', label: 'Genres' },
    { path: '/novels?sort=popular', label: 'Hot' },
    { path: '/novels?sort=newest', label: 'Latest' },
  ];

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        {/* Logo */}
        <Link to="/" className="logo">
          {domain?.logo ? (
            <img src={domain.logo} alt={siteName} className="logo-img" />
          ) : (
            <div className="logo-icon">
              <Book size={20} />
            </div>
          )}
          <span className="logo-text">{siteName}</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="nav-desktop">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Search Box */}
        <div className={`search-box ${isSearchOpen ? 'open' : ''}`}>
          <form onSubmit={handleSearch}>
            <Search className="search-icon" size={18} />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search novels, authors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {isSearchOpen && (
              <button type="button" className="search-close" onClick={closeSearch}>
                <X size={18} />
              </button>
            )}
          </form>
        </div>

        {/* Actions */}
        <div className="header-actions">
          <button className="action-btn search-toggle" onClick={toggleSearch}>
            <Search size={20} />
          </button>

          {isAuthenticated ? (
            <div className="user-menu-container" ref={userMenuRef}>
              <button
                className="user-btn"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="user-avatar">
                  {user?.avatar ? (
                    <img src={user.avatar} alt={user.username} />
                  ) : (
                    <User size={18} />
                  )}
                </div>
                <span className="user-name">{user?.username}</span>
              </button>

              {showUserMenu && (
                <div className="user-menu">
                  <Link to="/profile" className="menu-item" onClick={() => setShowUserMenu(false)}>
                    <User size={18} />
                    <span>Profile</span>
                  </Link>
                  <Link to="/bookmarks" className="menu-item" onClick={() => setShowUserMenu(false)}>
                    <Bookmark size={18} />
                    <span>Bookmarks</span>
                  </Link>
                  <Link to="/history" className="menu-item" onClick={() => setShowUserMenu(false)}>
                    <History size={18} />
                    <span>Reading History</span>
                  </Link>
                  <Link to="/settings" className="menu-item" onClick={() => setShowUserMenu(false)}>
                    <Settings size={18} />
                    <span>Settings</span>
                  </Link>
                  <div className="menu-divider"></div>
                  <button className="menu-item logout" onClick={handleLogout}>
                    <LogOut size={18} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="auth-buttons">
              <Link to="/login" className="btn-login">
                Login
              </Link>
              <Link to="/register" className="btn-register">
                Register
              </Link>
            </div>
          )}

          <button className="action-btn menu-toggle" onClick={toggleSidebar}>
            <Menu size={20} />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
